import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

// Deal stages - closed stages trigger closedAt timestamp
const CLOSED_STAGES = ['Won', 'Lost'];
const ACTIVE_STAGES = ['Contacts', 'Proposal', 'Negotiation'];

// Stage to position mapping (must match extension defaultPipeline.ts)
const STAGE_POSITION_MAP = {
  'Contacts': 0,
  'Proposal': 1,
  'Negotiation': 2,
  'Won': 3,
  'Lost': 4
};

// Helper to get stagePosition from stage name
const getStagePosition = (stage) => {
  return STAGE_POSITION_MAP[stage] ?? 0;
};

export function useDeals() {
  const { currentUser } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener with ownerId filter - exclude trashed deals
  useEffect(() => {
    if (!currentUser) {
      setDeals([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    // Don't use orderBy in the query to avoid issues with serverTimestamp()
    // We'll sort client-side instead
    const q = query(
      collection(db, 'deals'),
      where('ownerId', '==', currentUser.uid),
      where('trashed', '==', false)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const dealsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort client-side by createdAt descending
        // Handle null createdAt (new docs with serverTimestamp placeholder)
        dealsData.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setDeals(dealsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching deals:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Add new deal
  const addDeal = async (dealData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to add deals');
    }

    try {
      // Ensure contactIds is always an array
      const contactIds = Array.isArray(dealData.contactIds)
        ? dealData.contactIds
        : dealData.contactId
          ? [dealData.contactId]
          : [];

      // Calculate stagePosition from stage name for extension compatibility
      const stage = dealData.stage || 'Contacts';
      const stagePosition = getStagePosition(stage);

      const docRef = await addDoc(collection(db, 'deals'), {
        ...dealData,
        stage, // Ensure stage is always set
        stagePosition, // Required for extension filtering
        contactIds, // Use contactIds array instead of single contactId
        ownerId: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        closedAt: null,
        trashed: false,
        trashedAt: null
      });
      return docRef.id;
    } catch (err) {
      console.error('Error adding deal:', err);
      throw err;
    }
  };

  // Update existing deal
  const updateDeal = async (dealId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update deals');
    }

    try {
      const dealRef = doc(db, 'deals', dealId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      // Set closedAt timestamp when deal is won or lost
      // Reset closedAt to null when deal is moved back to active stages
      // Also sync stagePosition for extension compatibility
      if (updates.stage) {
        updateData.stagePosition = getStagePosition(updates.stage);

        if (CLOSED_STAGES.includes(updates.stage)) {
          updateData.closedAt = serverTimestamp();
        } else if (ACTIVE_STAGES.includes(updates.stage)) {
          updateData.closedAt = null;
        }
      }

      await updateDoc(dealRef, updateData);
    } catch (err) {
      console.error('Error updating deal:', err);
      throw err;
    }
  };

  // Soft delete deal (move to trash)
  const deleteDeal = async (dealId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to delete deals');
    }

    try {
      const dealRef = doc(db, 'deals', dealId);
      await updateDoc(dealRef, {
        trashed: true,
        trashedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error deleting deal:', err);
      throw err;
    }
  };

  // Restore deal from trash
  const restoreDeal = async (dealId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to restore deals');
    }

    try {
      const dealRef = doc(db, 'deals', dealId);
      await updateDoc(dealRef, {
        trashed: false,
        trashedAt: null,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error restoring deal:', err);
      throw err;
    }
  };

  // Permanently delete deal (for future trash view)
  const permanentlyDeleteDeal = async (dealId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to permanently delete deals');
    }

    try {
      const dealRef = doc(db, 'deals', dealId);
      await deleteDoc(dealRef);
    } catch (err) {
      console.error('Error permanently deleting deal:', err);
      throw err;
    }
  };

  return {
    deals,
    loading,
    error,
    addDeal,
    updateDeal,
    deleteDeal,
    restoreDeal,
    permanentlyDeleteDeal
  };
}
